class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}


class ChildRegistration {
  constructor() {
    this.initialized = false;
     this.userIsEditing = false;
    this.init();
  }

  init() {
    if (this.initialized) return;
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
    
    this.initialized = true;
  }

  setup() {
    this.setMaxDate();
    this.attachEventListeners();
    this.loadExistingData();
  }

  setMaxDate() {
    const dateInput = document.getElementById('childBirthday');
    if (dateInput) {
      const today = new Date().toISOString().split('T')[0];
      dateInput.setAttribute('max', today);
    }
  }

  attachEventListeners() {
    // Accordion toggle
    const header = document.querySelector('.child-registration-header');
    if (header && !header.dataset.listenerAttached) {
      header.addEventListener('click', this.toggleAccordion.bind(this));
      header.dataset.listenerAttached = 'true';
    }

    // Date picker wrapper click
    const dateWrapper = document.querySelector('.date-picker-wrapper');
    if (dateWrapper && !dateWrapper.dataset.listenerAttached) {
      dateWrapper.addEventListener('click', () => {
        const dateInput = document.getElementById('childBirthday');
        if (dateInput && dateInput.showPicker) {
          try {
            dateInput.showPicker();
          } catch (e) {
            // Fallback for browsers that don't support showPicker
            dateInput.focus();
          }
        }
      });
      dateWrapper.dataset.listenerAttached = 'true';
    }

    // Submit button
    const submitBtn = document.getElementById('childRegistrationSubmit');
    if (submitBtn && !submitBtn.dataset.listenerAttached) {
      submitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.validateAllFields()) {
          this.updateCartNote();
        }
      });
      submitBtn.dataset.listenerAttached = 'true';
    }

    const inputs = document.querySelectorAll('#childFirstName, #childBirthday, input[name="childGender"]');
    inputs.forEach(input => {
      if (!input.dataset.editListener) {
        input.addEventListener('input', () => {
          this.userIsEditing = true;
        });
        input.dataset.editListener = "true";
      }
    });


    // Checkout validation (if checkout button exists)
    this.attachCheckoutValidation();
  }

  toggleAccordion() {
    const accordion = document.getElementById('childRegistrationAccordion');
    if (accordion) {
      accordion.classList.toggle('active');
    }
  }

  validateAllFields() {
    const firstName = document.getElementById('childFirstName');
    const gender = document.querySelector('input[name="childGender"]:checked');
    const birthday = document.getElementById('childBirthday');

    if (!firstName || !firstName.value.trim()) {
      alert("❌ Please enter your child's name");
      if (firstName) firstName.focus();
      return false;
    }

    if (!gender) {
      alert("❌ Please select your child's gender");
      return false;
    }

    if (!birthday || !birthday.value) {
      alert("❌ Please select your child's birthday");
      if (birthday) birthday.focus();
      return false;
    }

    return true;
  }

  getChildData() {
    const firstName = document.getElementById('childFirstName');
    const gender = document.querySelector('input[name="childGender"]:checked');
    const birthday = document.getElementById('childBirthday');

    if (!firstName || !gender || !birthday) return null;

    const [year, month, day] = birthday.value.split('-');
    const formattedDate = `${month}/${day}/${year}`;

    return `--- Child Registration ---\nChild Name: ${firstName.value.trim()}\nGender: ${gender.value}\nBirthday: ${formattedDate}`;
  }

  updateCartNote() {
    const childData = this.getChildData();
    if (!childData) return;

    const statusDiv = document.getElementById('childRegistrationStatus');
    const submitBtn = document.getElementById('childRegistrationSubmit');

    if (submitBtn) {
      submitBtn.textContent = 'Saving...';
      submitBtn.disabled = true;
      submitBtn.style.opacity = '0.7';
    }

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: childData })
    })
    .then(response => response.json())
    .then(cart => {
      console.log('Child registration saved:', cart.note);
      
      // Show success message
      if (statusDiv) {
        statusDiv.style.display = 'block';
        setTimeout(() => {
          statusDiv.style.display = 'none';
        }, 3000);
      }

      // Update icon to green checkmark
      this.showCompletedIcon();

      // Close accordion
      const accordion = document.getElementById('childRegistrationAccordion');
      if (accordion) {
        accordion.classList.remove('active');
      }

      // Reset button
      if (submitBtn) {
        submitBtn.textContent = 'Submit';
        submitBtn.disabled = false;
        submitBtn.style.opacity = '1';
      }
    })
    .catch(err => {
      console.error('Error saving child registration:', err);
      alert('❌ Error saving details. Please try again.');
      
      if (submitBtn) {
        submitBtn.textContent = 'Submit';
        submitBtn.disabled = false;
        submitBtn.style.opacity = '1';
      }
    });
  }

  showCompletedIcon() {
    const giftIcon = document.getElementById('registrationIcon');
    const completedIcon = document.getElementById('registrationIconCompleted');
    
    if (giftIcon) giftIcon.style.display = 'none';
    if (completedIcon) completedIcon.style.display = 'block';
  }

  showGiftIcon() {
    const giftIcon = document.getElementById('registrationIcon');
    const completedIcon = document.getElementById('registrationIconCompleted');
    
    if (giftIcon) giftIcon.style.display = 'block';
    if (completedIcon) completedIcon.style.display = 'none';
  }

  loadExistingData() {
  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {
      const note = cart.note || '';

      // If user is editing -> DO NOT overwrite form
      if (this.userIsEditing) {
        return;
      }

      if (note.includes('Child Registration')) {
        this.prefillForm(note);
        this.showCompletedIcon();
      } else {
        this.showGiftIcon();
      }
    })
    .catch(err => console.error('Error loading cart data:', err));
}


  prefillForm(note) {
    const nameMatch = note.match(/Child Name:\s*(.*)/);
    const genderMatch = note.match(/Gender:\s*(.*)/);
    const birthdayMatch = note.match(/Birthday:\s*(\d{2})\/(\d{2})\/(\d{4})/);

    const firstName = document.getElementById('childFirstName');
    if (nameMatch && firstName) {
      firstName.value = nameMatch[1].trim();
    }

    if (genderMatch) {
      const genderValue = genderMatch[1].trim();
      const genderRadio = document.querySelector(`input[name="childGender"][value="${genderValue}"]`);
      if (genderRadio) genderRadio.checked = true;
    }

    if (birthdayMatch) {
      const month = birthdayMatch[1];
      const day = birthdayMatch[2];
      const year = birthdayMatch[3];
      const birthday = document.getElementById('childBirthday');
      if (birthday) {
        birthday.value = `${year}-${month}-${day}`;
      }
    }
  }

  attachCheckoutValidation() {
    const checkoutButton = document.getElementById('CartDrawer-Checkout');
    if (checkoutButton && !checkoutButton.dataset.childRegValidation) {
      checkoutButton.addEventListener('click', (e) => {
        if (!this.validateAllFields()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true);
      checkoutButton.dataset.childRegValidation = 'true';
    }

    const cartForm = document.getElementById('CartDrawer-Form');
    if (cartForm && !cartForm.dataset.childRegValidation) {
      cartForm.addEventListener('submit', (e) => {
        if (!this.validateAllFields()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true);
      cartForm.dataset.childRegValidation = 'true';
    }
  }

  // Public method to reinitialize after cart update
  reinitialize() {
    this.setup();
  }
}

// Initialize Child Registration
let childRegistrationInstance = null;

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    childRegistrationInstance = new ChildRegistration();
  });
} else {
  childRegistrationInstance = new ChildRegistration();
}

// Re-initialize after cart updates (for AJAX cart updates)
if (typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    setTimeout(() => {
      initCartOfferSwiper();
      if (childRegistrationInstance) {
        childRegistrationInstance.userIsEditing = false; // RESET EDIT MODE
        childRegistrationInstance.reinitialize();
      }
    }, 100);
  });
}

// Alternative: Listen to custom cart update events
document.addEventListener('cart:updated', () => {
  setTimeout(() => {
    if (childRegistrationInstance) {
      childRegistrationInstance.reinitialize();
    }
  }, 100);
});

// MutationObserver fallback for cart drawer updates
const observeCartDrawer = () => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const observer = new MutationObserver(() => {
      if (childRegistrationInstance && document.getElementById('childRegistrationAccordion')) {
        childRegistrationInstance.reinitialize();
      }
    });

    observer.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }
};

// Start observing
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeCartDrawer);
} else {
  observeCartDrawer();
}

// Export for global access if needed
window.ChildRegistration = ChildRegistration;
// Function to initialize or re-initialize the offer swiper
function initCartOfferSwiper() {
  const swiperContainer = document.querySelector('.offers-swiper-cart');
  if (!swiperContainer) return;

  // Destroy previous instance if it exists
  if (swiperContainer.swiper) {
    swiperContainer.swiper.destroy(true, true);
  }

  // Initialize Swiper
  new Swiper(swiperContainer, {
    direction: 'vertical',
    loop: true,
    autoplay: {
      delay: 1800,
      disableOnInteraction: false,
    },
    slidesPerView: 1,
    allowTouchMove: true,
  });
}

// Initialize swiper on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartOfferSwiper);
} else {
  initCartOfferSwiper();
}